import { LucideIcon, ChevronDown } from "lucide-react";
import cn from "../../../utils/styleHelpers";

interface SidebarNavItemProps {
    icon: LucideIcon;
    label: string;
    active?: boolean;
    badge?: string;
    badgeColor?: "green" | "red" | "yellow" | "blue";
    onClick?: () => void;
    children?: React.ReactNode;
    opened?: boolean;
    collapsed?: boolean;
}

const badgeColors = {
    green: "bg-muted text-muted-foreground",
    red: "bg-destructive/10 text-destructive",
    yellow: "bg-muted text-muted-foreground",
    blue: "bg-accent-tint text-primary",
};

const SidebarNavItem = ({
    icon: Icon,
    label,
    active = false,
    badge,
    badgeColor = "blue",
    onClick,
    children,
    opened,
    collapsed,
}: SidebarNavItemProps) => {
    const showChildren = Boolean(children && opened && !collapsed);

    return (
        <div className="w-full">
            <button
                type="button"
                onClick={onClick}
                title={collapsed ? label : undefined}
                aria-label={label}
                aria-expanded={children ? !!opened : undefined}
                className={cn(
                    "w-full flex items-center px-1.5 h-7 rounded-[5px] text-[13px] font-normal transition-colors",
                    collapsed ? "justify-center" : "gap-2",
                    active
                        ? "bg-sidebar-active text-sidebar-text-active"
                        : "text-sidebar-text hover:bg-sidebar-hover",
                )}
            >
            <Icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && (
                <div className="flex-1 min-w-0 flex items-center justify-between w-full">
                    <span className="truncate text-left font-normal">{label}</span>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        {badge && (
                            <span
                                className={cn(
                                    "px-1.5 py-0.5 text-[10px] font-medium rounded",
                                    badgeColors[badgeColor]
                                )}
                            >
                                {badge}
                            </span>
                        )}
                        {children && (
                            <ChevronDown
                                className={cn("h-4 w-4 transition-transform", opened ? "rotate-0" : "-rotate-90")}
                            />
                        )}
                    </div>
                </div>
            )}
            </button>

            {showChildren && (
                <div className="ml-3 mt-1 space-y-0.5 border-l border-border pl-2.5">
                    {children}
                </div>
            )}
        </div>
    );
};

export default SidebarNavItem;
